import {Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleInfo} from '@fortawesome/pro-solid-svg-icons';
import {
  BicBadgeComponent,
  BicDropdownElementComponent,
  BicSelectComponent,
} from '@buildinginformationcloud/bic-ui-components';
import {StateConfigService} from '../../../../services/state/state-config.service';
import {AiModelEnterprisePopupComponent} from './ai-model-enterprise-popup.component';

/**
 * Auswahl des KI-Modells eines Prüfregelsets – für die KI-Prüfung und die hybride Prüfung
 * gleichermaßen (beide Editoren hatten den Block vorher als identische Kopie).
 *
 * Standardkunden haben genau ein Modell. Dann ist ein Dropdown eine Auswahl, die es nicht gibt:
 * angezeigt wird stattdessen ein Badge plus der Hinweis auf das Enterprise-Angebot. Erst wenn in
 * der FeatureConfig weitere Modelle freigeschaltet sind, erscheint wieder ein echtes Dropdown.
 *
 * Die Liste kommt aus dem bereits geladenen State (`featureConfig.aiModels`) und nicht aus einem
 * eigenen Request – das Backend liefert sie mandantenspezifisch aufgelöst, inklusive Standardmodell.
 *
 * Rein anzeigend: den Vorgabewert für ein neues Regelset setzt der Editor selbst über
 * `StateConfigService.getDefaultAiModel()`. Würde die Komponente ihn beim Init emittieren, änderte
 * sie während desselben Change-Detection-Laufs einen Wert des Elternteils (NG0100).
 */
@Component({
  selector: 'app-ai-model-field',
  standalone: true,
  imports: [
    FaIconComponent, BicBadgeComponent, BicSelectComponent, BicDropdownElementComponent,
    AiModelEnterprisePopupComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './ai-model-field.component.html',
})
export class AiModelFieldComponent {
  private stateConfigService = inject(StateConfigService);
  private router = inject(Router);

  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();

  protected showEnterprisePopup = false;
  protected readonly faCircleInfo = faCircleInfo;

  /** Für diesen Nutzer bzw. seine Organisation freigeschaltete Modelle. */
  private get allowedModels(): string[] {
    return this.stateConfigService.getAiModels();
  }

  /**
   * Ein bereits gespeichertes, inzwischen nicht mehr freigeschaltetes Modell bleibt wählbar und
   * steht vorn – sonst würde das Öffnen eines Bestandsregelsets es stillschweigend umstellen.
   */
  get options(): string[] {
    const allowed = this.allowedModels;
    if (this.value && !allowed.includes(this.value)) {
      return [this.value, ...allowed];
    }
    return allowed;
  }

  /** Mehr als ein Modell heißt: es gibt wirklich etwas zu wählen. */
  get hasChoice(): boolean {
    return this.options.length > 1;
  }

  /** Anzeigewert des Badges, solange keine Auswahl besteht. */
  get displayedModel(): string {
    return this.value || this.options[0] || '';
  }

  onSelect(model: string): void {
    this.value = model;
    this.valueChange.emit(model);
  }

  openEnterprisePopup(): void {
    this.showEnterprisePopup = true;
  }

  onEnterprisePopupClosed(): void {
    this.showEnterprisePopup = false;
  }

  onOfferRequested(): void {
    this.showEnterprisePopup = false;
    this.router.navigate(['/checkout']);
  }
}
